// Affinity Kitchens
import * as _ from 'lodash'
import globalRelay, { WellKnownGlobalFunction } from 'Scripts/globalRelay';
import util from 'Scripts/util';
import enums from 'Scripts/enum';
import store, { getModel, getCollection, ModelCollection } from 'Scripts/store';
import * as actions from 'Scripts/actions';
import * as contractorActions from 'Scripts/contractorActions';
import * as vendorActions from 'Scripts/vendorActions';
import JobCostSummary from 'Scripts/models/jobCostSummary';
import { IJobCost } from 'Scripts/models/jobCostModel';
import { getInstallSheets } from 'Scripts/schemaHelper';
import { PatchOpType } from 'Scripts/modelFactory';
import { IJobCostLineItem } from 'Scripts/models/jobCostLineItemModel';

const ACCOUNTING_SUMMARY_FACTORIES = [
	(jobCost, summary, designRetainerAmount) => { return { group: 'accounting', type: 'budget-revenue', description: 'Revenue - Cabinets', itemNote: 'REV CAB', sellPrice: (summary.orderSellPrice + summary.builderUpgradeSellPrice + summary.clientUpgradeSellPrice) - designRetainerAmount, cost: 0 }; },
	(jobCost, summary, designRetainerAmount) => { return { group: 'accounting', type: 'budget-revenue', description: 'Revenue - Cabinets', itemNote: 'REV CAB', sellPrice: designRetainerAmount, cost: 0 }; },
	(jobCost, summary) => { return { group: 'accounting', type: 'budget-revenue', description: 'Revenue - Renovation', itemNote: 'REV RENO', sellPrice: summary.subtradeSellPrice, cost: 0 }; },
	(jobCost, summary) => { return { group: 'accounting', type: 'budget-revenue', description: 'Revenue - Solid Surface Countertops', itemNote: 'REV CTOP', sellPrice: summary.solidSurfaceCounterSellPrice, cost: 0 }; },
	(jobCost, summary) => { return { group: 'accounting', type: 'budget-cost', description: 'Job Cost - Cabinets', itemNote: 'COS CAB', cost: (summary.orderCabinetCost - summary.orderCabinetFreightCost), sellPrice: 0 }; },
	(jobCost, summary) => { return { group: 'accounting', type: 'budget-cost', description: 'Job Cost - Outside Supplier', itemNote: 'COS OUTSUP', cost: summary.orderOutsideSupplierCost, sellPrice: 0 }; },
	(jobCost, summary) => { return { group: 'accounting', type: 'budget-cost', description: 'Job Cost - Warehouse Parts', itemNote: 'COS WHSPRT', cost: summary.orderWarehouseCost, sellPrice: 0 }; },
	//(jobCost, summary) => { return { group: 'accounting', type: 'budget-cost', description: '', itemNote: 'COS MISC', cost: summary.orderMiscCost, sellPrice: 0 }; }, // DAR - description is left empty to ensure that the write to accumatica fails - misc needs review by a human
	(jobCost, summary) => { return { group: 'accounting', type: 'budget-cost', description: 'Job Cost - Glass', itemNote: 'COS GLASS', cost: summary.orderGlassCost + summary.orderSpecialGlassCost, sellPrice: 0 }; },
	(jobCost, summary) => {
		const trashDisposal = _.filter(summary.orderCalculatedFeeItems, li => li.description === 'Trash Disposal').reduce((sum, li) => sum + li.cost, 0);
		let condoPremium = 0;

		if (jobCost.getSettingValue(enums.jobCostSettingGroups.General, 'applyCondoPremium') === '1') {
			condoPremium = util.roundAsCurrency(0.5 * summary.orderInstallationCost);
		}
		return { group: 'accounting', type: 'budget-cost', description: 'Job Cost - Installation Cabinets', itemNote: 'COS IN CAB', cost: summary.orderInstallationCost + trashDisposal + condoPremium, sellPrice: 0 };
	},
	//(jobCost, summary) => { return { group: 'accounting', type: 'budget-cost', description: '', itemNote: 'COS IN SV', cost: summary.orderInstallServiceCost, sellPrice: 0 }; }, // DAR - description is left empty to ensure that the write to accumatica fails - it is undecided how to handle this
	(jobCost, summary) => { return { group: 'accounting', type: 'budget-cost', description: 'Job Cost - Installation Travel', itemNote: 'COS IN TRV', cost: summary.orderTravelCost, sellPrice: 0 }; },
	(jobCost, summary) => {
		let condoPremium = 0;

		if (jobCost.getSettingValue(enums.jobCostSettingGroups.General, 'applyCondoPremium') === '1') {
			condoPremium = util.roundAsCurrency(0.5 * summary.orderDeliveryCost);
		}
		return { group: 'accounting', type: 'budget-cost', description: 'Job Cost - Delivery', itemNote: 'COS DELIV', cost: summary.orderDeliveryCost + condoPremium, sellPrice: 0 };
	},
	(jobCost, summary) => { return { group: 'accounting', type: 'budget-cost', description: 'Job Cost - Referral Fee', itemNote: 'COS REFER', cost: summary.orderReferralFeeCost }; },
	(jobCost, summary) => { return { group: 'accounting', type: 'budget-cost', description: 'Job Cost - Processing Fee', itemNote: 'COS PROC', cost: _.filter(summary.orderCalculatedFeeItems, li => li.description === 'Processing Fee').reduce((sum, li) => sum + li.cost, 0), sellPrice: 0 }; },
	(jobCost, summary) => { return { group: 'accounting', type: 'budget-cost', description: 'Job Cost - Chargeable Service & Warranty', itemNote: 'COS SV WTY', cost: _.filter(summary.orderWarrantyItems, { isPreSubtotal: true }).reduce((sum, li) => sum + li.cost, 0), sellPrice: 0 }; },
	(jobCost, summary) => { return { group: 'accounting', type: 'budget-cost', description: 'Job Cost - Alberta Renovation Warranty', itemNote: 'COS ABRENO', cost: _.filter(summary.orderWarrantyItems.concat(summary.solidSurfaceCounterWarrantyItems, summary.subtradeWarrantyItems), li => li.type === 'warranty' && li.description === 'Alberta Reno Warranty').reduce((sum, li) => sum + li.cost, 0), sellPrice: 0 }; },
	(jobCost, summary) => { return { group: 'accounting', type: 'budget-cost', description: 'Job Cost - Subtrades (Retail)', itemNote: 'COS RENSUB', cost: summary.subtradeCost - _.filter(jobCost.lineItems, li => ['subtrade'].indexOf(li.type) !== -1 && !li.isOptional && li.subTradeTypeKey === 'waste-disposal').reduce((sum, li) => sum + li.totalCost, 0), sellPrice: 0 }; },
	(jobCost, summary) => { return { group: 'accounting', type: 'budget-cost', description: 'Job Cost - Solid Surface Countertops', itemNote: 'COS CTOP', cost: summary.solidSurfaceCounterCost, sellPrice: 0 }; },
	(jobCost, summary) => { return { group: 'accounting', type: 'budget-cost', description: 'Job Cost - Shipping', itemNote: 'COS SHIP', cost: summary.orderCabinetFreightCost, sellPrice: 0 }; },
	(jobCost) => { return { group: 'accounting', type: 'budget-cost', description: 'Job Cost - Renovation Garbage Bin', itemNote: 'COS BIN', cost: _.filter(jobCost.lineItems, li => ['subtrade'].indexOf(li.type) !== -1 && !li.isOptional && li.subTradeTypeKey === 'waste-disposal').reduce((sum, li) => sum + li.totalCost, 0), sellPrice: 0 }; },
	(jobCost, summary) => { return { group: 'accounting', type: 'budget-cost', description: 'Job Cost - Overhead Allowance', itemNote: 'COS OVERHD', cost: _.filter(summary.orderCalculatedFeeItems, li => li.description === 'Overhead Allowance').reduce((sum, li) => sum + li.cost, 0), sellPrice: 0 }; },
	(jobCost, summary) => { return { group: 'accounting', type: 'budget-cost', description: 'Job Cost - PST Expense', itemNote: 'COS PST', cost: _.filter(summary.orderTaxItems, li => li.type === 'tax' && li.isOptional && li.isPreSubtotal).reduce((sum, li) => sum + li.cost, 0), sellPrice: 0 }; }
];

// X Glass is not appearing - probably need to include special order glass
// X Travel is not appearing
// X Service & warranty is not appearing
// X Trash Disposal from install sheet needs to end up in COS IN CAB
// X Condo Premium needs to be split per the source calculation between COS IN CAB and COS DELIV

const globalConfig = globalRelay.config;
let _warehouseFetched = false;

globalRelay.on(globalRelay.APP_BOOTSTRAPPED, function ({ authenticated }) {
	if (authenticated) {
		vendorActions.fetchVendors({ type: enums.vendorTypes.Internal_Warehouse });
		_warehouseFetched = true;
	}
});

globalRelay.on(globalRelay.APP_AUTH_CHANGE, function ({ currentUser }) {
	if (!_warehouseFetched && currentUser) {
		vendorActions.fetchVendors({ type: enums.vendorTypes.Internal_Warehouse });
		_warehouseFetched = true;
	}
});

globalRelay.on(globalRelay.JOB_COST_CREATE, function (jobCost) {
	const job = getModel({ collection: ModelCollection.jobs, key: jobCost.jobKey });

	jobCost.setSetting(enums.jobCostSettingGroups.General, 'glassLaborCharge', undefined, globalConfig.jobCostSettings.glass.laborCharge);
	jobCost.setSetting(enums.jobCostSettingGroups.General, 'applyCondoPremium', undefined, '0');

	if (job && job.workSiteAddress) {
		const jurisdictions = globalConfig.jobCostSettings.tax.jurisdictions;
		const isOutOfState = !job.workSiteAddress.region || ['az', 'arizona'].indexOf(job.workSiteAddress.region.toLowerCase()) === -1;
		const jurisdictionName = isOutOfState ? 'out of state' : job.workSiteAddress.city && job.workSiteAddress.city.toLowerCase();
		const jurisdiction = _.find(jurisdictions, j => j.name.toLowerCase() === jurisdictionName);
		const taxRate = jurisdiction && jurisdiction.rate || 0;

		if (jurisdiction) {
			jobCost.setSetting(enums.jobCostSettingGroups.General, 'taxJurisdiction', undefined, jurisdiction.name);
		}
		jobCost.setSetting(enums.jobCostSettingGroups.General, 'taxRate', undefined, taxRate);
	}

	/**
	 * @since https://veridical.atlassian.net/browse/RELAY-2328 
	 * 
	 * @todo fix this after ops is finished
	 * @see https://veridical.atlassian.net/browse/RELAY-2329
	 */
	getInstallSheets(jobCost).toArray().forEach(sheet => {
		handleGeneratorGasInstallItemDefault(jobCost, sheet.key);
	});
});


globalRelay.on(globalRelay.JOB_COST_UPDATE, function (jobCost: IJobCost, summary, updatePath) {

	/**
		 * @since https://veridical.atlassian.net/browse/RELAY-2328 
		 * 
		 * @todo fix this after ops is finished
		 * @see https://veridical.atlassian.net/browse/RELAY-2329
		 */
	const storedJC = getModel({ collection: ModelCollection.jobCosts, key: jobCost.key });
	if (storedJC) {
		const addInstallSheetPathRegex = /^\/installSheets\/[0-9A-Fa-f]{8}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{12}$/i
		const addInstallSheetPatch = jobCost.getPatch(storedJC).find(patch => (patch.op === PatchOpType.add || PatchOpType.remove) && addInstallSheetPathRegex.test(patch.path))
		if (addInstallSheetPatch?.op === PatchOpType.add) {
			handleGeneratorGasInstallItemDefault(jobCost, addInstallSheetPatch.value.key);
		} else if (addInstallSheetPatch?.op === PatchOpType.remove) {
			const installSheetKey = addInstallSheetPatch.path.split('/')[2]
			assignSupplyOnly(jobCost, installSheetKey);
			handleInstallKit(jobCost, installSheetKey)
		} else {
			assignSupplyOnly(jobCost);
		}
	} else {
		assignSupplyOnly(jobCost);

	}

});

/**
 * @since https://veridical.atlassian.net/browse/RELAY-2328
 * 
 * @todo fix this after ops is finished
 * @see https://veridical.atlassian.net/browse/RELAY-2329
 */
function handleGeneratorGasInstallItemDefault(jobCost: IJobCost, installSheetKey: string) {
	const job = getModel({ collection: ModelCollection.jobs, key: jobCost.jobKey });
	if (!job.isRenovation) {
		jobCost.withStrictFilters(strictJobCost => {
			const installSheet = getInstallSheets(strictJobCost).find(sheet => sheet.key === installSheetKey)
			const installationUnitPrice = installSheet.unitCost || strictJobCost.installationUnitPrice || 0;
			const installationConfig = actions.getInstallationConfig(strictJobCost)?.installationConfig
			const installationItems = installationConfig.items
			const generatorGasConfigItem = installationItems.find(item => item.calculation === 'generatorGas')
			const sortIndex = installationItems.indexOf(generatorGasConfigItem)
			const generatorGasLineItem = _.assign(({ key: util.generateGuid(), cost: typeof generatorGasConfigItem.cost !== 'undefined' ? Number(generatorGasConfigItem.cost) : Number(generatorGasConfigItem.costFactor) * installationUnitPrice, installSheetKey, quantity: 1, sortIndex: sortIndex, doorSizeKey: undefined } as IJobCostLineItem), generatorGasConfigItem);
			strictJobCost.lineItems.add(generatorGasLineItem);
		});
	}
}

/**
	 * @since https://veridical.atlassian.net/browse/RELAY-2328 
	 * 
	 * @todo fix this after ops is finished
	 * @see https://veridical.atlassian.net/browse/RELAY-2329
	 */
function handleInstallKit(jobCost: IJobCost, installSheetKey: string = undefined) {
	const variant = jobCost.companyVariant;
	jobCost.withStrictFilters(strictJobCost => {
		const totalCabinetsLines = strictJobCost.lineItems.filter(item => item.isCabinetCount === true && item.installSheetKey !== installSheetKey);
		const totalCabinets = _.reduce(totalCabinetsLines, (sum, l) => sum + (l.quantity || 0), 0);
		const warehouseVendor = getCollection({ collection: ModelCollection.vendors }).find(v => v.type === enums.vendorTypes.Internal_Warehouse);
		const installKitProduct = warehouseVendor.getProductBySku('InstallKit');

		// ensure the InstallKit line item
		if (installKitProduct && variant !== 'tucson' && jobCost.getSettingValue(enums.jobCostSettingGroups.General, 'installKitRemoved', undefined) !== '1') {
			const installKitItem = _.find(strictJobCost.lineItems, { productKey: installKitProduct.key }) || strictJobCost.lineItems.add({ contractVisibility: enums.jobCostLineItemReportingFlags.Contract_Hidden, description: installKitProduct.name, isSystemManaged: true, itemNote: 'One kit is automatically added for every 20 boxes as determined by the Installation & Service screen', margin: jobCost.margin || 0, productKey: installKitProduct.key, productName: installKitProduct.sku, type: 'warehouse', vendorKey: warehouseVendor.key });
			if (totalCabinets > 0) {
				installKitItem.quantity = Math.ceil(totalCabinets / 20);
				installKitItem.cost = installKitProduct.cost.toFixed(2);
				installKitItem.sellPrice = Number(util.costCalculator.getSellWithCostAndMargin(installKitItem.totalCost, installKitItem.margin));
			}
			else if (installKitItem) {
				jobCost.lineItems.delete(installKitItem.key);
			}
		}
	});
}

/** 
Can't use summary values since they are not updated until after the current save loop.
This led to reusing the underlying logic from 
src/Legacy.CMS.Web/Scripts/components/jobCostEditInstallationService.tsx@690 (as of 6/20/2024)
to calculate installation and delivery cost totals for jobcosts from their line items.
*/
function assignSupplyOnly(jobCost, /**
	* @since https://veridical.atlassian.net/browse/RELAY-2328
	* 
	* @todo fix this after ops is finished
	* @see https://veridical.atlassian.net/browse/RELAY-2329
	*/ removedInstallSheetKey = undefined) {
	if (!jobCost.currentChangeOrder) {
		let installationCost = 0;
		jobCost.withStrictFilters(strictJobCost => {
			_.forEach(strictJobCost.getLineItemsByType(['installation', 'installation-custom']), item => {
				if (['installation', 'installation-custom'].indexOf(item.type) !== -1 && item.installSheetKey !== removedInstallSheetKey) {
					installationCost += item.totalCost || 0;
				}
			});
		});
		jobCost.setSetting(enums.jobCostSettingGroups.General, 'supplyOnly', undefined, +(installationCost <= 0));
	}
}


globalRelay.on(globalRelay.JOB_COST_LINE_UPDATE, function (lineItem, updatePath, jobCost, deleted) {
	const storedJC = getModel({ collection: ModelCollection.jobCosts, key: jobCost.key });
	const variant = jobCost.companyVariant;
	if (lineItem.type === 'glass') {
		const glassLaborSetting = jobCost.getSetting(enums.jobCostSettingGroups.General, 'glassLaborCharge', undefined, true);
		const laborFee = Number(glassLaborSetting.value);

		if (['glassTypeKey', 'doorSizeKey'].indexOf(updatePath) !== -1) {
			// add the labor fee to glass if appropriate
			if (lineItem.cost) {
				lineItem.cost += laborFee;
				lineItem.sellPrice = (lineItem.margin > 0) ? util.roundAsCurrency(util.costCalculator.getSellWithCostAndMargin(lineItem.totalCost, lineItem.margin)) : 0;
			}
		}
	}
	else if (['outside-supplier'].indexOf(lineItem.type) !== -1) {
		// default the costFactor for outside supplier items
		lineItem.costFactor = 0.125;
	}
	else if (deleted && lineItem.type === 'warehouse') {
		const warehouseVendor = getCollection({ collection: ModelCollection.vendors }).find(v => v.type === enums.vendorTypes.Internal_Warehouse);
		const installKitProduct = warehouseVendor.getProductBySku('InstallKit');

		if (lineItem.productKey === installKitProduct.key && lineItem.isSystemManaged) {
			jobCost.setSetting(enums.jobCostSettingGroups.General, 'installKitRemoved', undefined, '1');
		}
	}
	else if (['installation', 'installation-custom'].indexOf(lineItem.type) !== -1) {
		const installSheet = getInstallSheets(jobCost)[lineItem.installSheetKey];
		const installSheetKey = installSheet.key;
		const installationUnitPrice = installSheet.unitCost || jobCost.installationUnitPrice || 0;
		const installationMargin = installSheet.margin || jobCost.installationMargin || 0;
		const installationVersion = globalConfig.jobCostSettings.installation.configVersion;
		const installConfigName = jobCost.getSettingValue(enums.jobCostSettingGroups.General, 'installationConfigDefinition', undefined, `installation-${installationVersion}`);
		const installationConfig = store().getIn(['model', 'config', _.camelCase(installConfigName)]);

		jobCost.withStrictFilters(strictJobCost => {
			const totalCabinetsLines = _.filter(strictJobCost.lineItems, { isCabinetCount: true });
			const totalInstallSheetCabinetsLines = _.filter(totalCabinetsLines, { installSheetKey });
			const totalCabinets = _.reduce(totalCabinetsLines, (sum, l) => sum + (l.quantity || 0), 0);
			const totalInstallSheetCabinets = _.reduce(totalInstallSheetCabinetsLines, (sum, l) => sum + (l.quantity || 0), 0);
			const warehouseVendor = getCollection({ collection: ModelCollection.vendors }).find(v => v.type === enums.vendorTypes.Internal_Warehouse);
			const installKitProduct = warehouseVendor.getProductBySku('InstallKit');

			// ensure the InstallKit line item
			if (installKitProduct && variant !== 'tucson' && jobCost.getSettingValue(enums.jobCostSettingGroups.General, 'installKitRemoved') !== '1') {
				const installKitItem = _.find(strictJobCost.lineItems, { productKey: installKitProduct.key }) || strictJobCost.lineItems.add({ contractVisibility: enums.jobCostLineItemReportingFlags.Contract_Hidden, description: installKitProduct.name, isSystemManaged: true, itemNote: 'One kit is automatically added for every 20 boxes as determined by the Installation & Service screen', margin: jobCost.margin || 0, productKey: installKitProduct.key, productName: installKitProduct.sku, type: 'warehouse', vendorKey: warehouseVendor.key });
				if (totalCabinets > 0) {
					installKitItem.quantity = Math.ceil(totalCabinets / 20);
					installKitItem.cost = installKitProduct.cost.toFixed(2);
					installKitItem.sellPrice = Number(util.costCalculator.getSellWithCostAndMargin(installKitItem.totalCost, installKitItem.margin));
				}
				else if (installKitItem) {
					jobCost.lineItems.delete(installKitItem.key);
				}
			}

			// process our sub-total items first
			let calculatedItems = _.filter(installationConfig.items, item => !!item.calculation && !item.excludeFromSubtotal);

			_.forEach(calculatedItems, configItem => {
				const calculatedLineItem = _.find(strictJobCost.lineItems, { type: configItem.type, group: configItem.group, description: configItem.description, installSheetKey }) || strictJobCost.lineItems.add(configItem);

				calculatedLineItem.installSheetKey = installSheet.key;
				calculatedLineItem.sortIndex = installationConfig.items.indexOf(configItem);
				calculatedLineItem.margin = installationMargin;

				if (configItem.calculation === 'perCabinet') {
					calculatedLineItem.quantity = totalInstallSheetCabinets;
					calculatedLineItem.cost = util.roundAsCurrency(calculatedLineItem.costFactor * installationUnitPrice);
				}
				else if (configItem.calculation === 'perTenCabinets') {
					calculatedLineItem.quantity = totalInstallSheetCabinets / 10;
					calculatedLineItem.cost = util.roundAsCurrency(calculatedLineItem.costFactor * installationUnitPrice);
				}

				calculatedLineItem.sellPrice = util.roundAsCurrency(util.costCalculator.getSellWithCostAndMargin(calculatedLineItem.totalCost, calculatedLineItem.margin));

				if (calculatedLineItem.sellPrice <= 0) {
					strictJobCost.lineItems.delete(calculatedLineItem.key);
				}
			});

			// process our non-sub-total items next
			const customItems = _.filter(strictJobCost.lineItems, { type: 'installation-custom', installSheetKey });
			const customSubTotal = _.reduce(customItems, (sum, l) => sum + (l.totalCost || 0), 0);
			const subTotalItems = _.filter(installationConfig.items, item => !item.excludeFromSubtotal && item.type !== "travel");
			const subTotal = _.reduce(subTotalItems, (sum, l) => {
				const orderItem = _.find(strictJobCost.lineItems, { type: l.type, group: l.group, description: l.description, installSheetKey });
				return sum + (orderItem && orderItem.totalCost || 0);
			}, customSubTotal);
			calculatedItems = _.filter(installationConfig.items, item => !!item.calculation && !!item.excludeFromSubtotal && item.calculation !== 'enforceMinimum');
			_.forEach(calculatedItems, configItem => {
				let calculatedLineItem = _.find(strictJobCost.lineItems, { type: configItem.type, group: configItem.group, description: configItem.description, installSheetKey });

				// don't auto-generate line items unless told - boolean installation items use quantity as the on/off flag and auto-generating will default quantity to 1 (on)
				if (!calculatedLineItem && configItem.autoGenerate) {
					calculatedLineItem = strictJobCost.lineItems.add(configItem);
				}

				if (calculatedLineItem) {
					calculatedLineItem.installSheetKey = installSheetKey;
					calculatedLineItem.sortIndex = installationConfig.items.indexOf(configItem);
					calculatedLineItem.margin = installationMargin;

					if (configItem.calculation === 'surcharge' || configItem.calculation === 'percentOfInstall') {
						calculatedLineItem.cost = util.roundAsCurrency(subTotal * configItem.percent);
					}
					else if (configItem.calculation === 'travel') {
						calculatedLineItem.cost = util.roundAsCurrency(Math.ceil(Math.max(configItem.minDays, subTotal / configItem.subTotalDivisor)) * configItem.perDiem);
					}
					else if (configItem.calculation === 'paintedCabinets') {
						const paintedCabinetLines = _.filter(strictJobCost.lineItems, { group: 'Cabinets', installSheetKey });
						const paintedCabinetLinesTotal = _.reduce(paintedCabinetLines, (sum, l) => sum + (l.quantity || 0), 0);

						calculatedLineItem.cost = util.roundAsCurrency(Number(configItem.amountPerItem) * Number(paintedCabinetLinesTotal));
					}
					else if (configItem.calculation === 'generatorGas' && storedJC) {
						calculatedLineItem.cost = util.roundAsCurrency(Math.ceil(Math.max(configItem.minDays, subTotal / configItem.subTotalDivisor)) * configItem.perDiem);
					}

					calculatedLineItem.sellPrice = util.roundAsCurrency(util.costCalculator.getSellWithCostAndMargin(calculatedLineItem.totalCost, calculatedLineItem.margin));
				}
			});

			// finally process the enforceMinimum
			calculatedItems = _.filter(installationConfig.items, item => item.calculation === 'enforceMinimum');
			_.forEach(calculatedItems, configItem => {
				const calculatedLineItem = _.find(strictJobCost.lineItems, { type: configItem.type, group: configItem.group, description: configItem.description, installSheetKey });

				if (calculatedLineItem) {
					const minPayLines = _.filter(strictJobCost.lineItems, li => li.key !== calculatedLineItem.key && ['installation', 'installation-custom'].indexOf(li.type) !== -1 && li.installSheetKey === installSheetKey);
					const minPayTotal = _.reduce(minPayLines, (sum, l) => sum + (l.totalCost || 0), 0);
					const minPayDiff = configItem.minimumAmount - minPayTotal;

					calculatedLineItem.sortIndex = installationConfig.items.indexOf(configItem);
					calculatedLineItem.margin = installationMargin;
					calculatedLineItem.cost = minPayDiff > 0 ? util.roundAsCurrency(minPayDiff) : 0;

					calculatedLineItem.sellPrice = util.roundAsCurrency(util.costCalculator.getSellWithCostAndMargin(calculatedLineItem.totalCost, calculatedLineItem.margin));
				}
			});
		});
	}
});

globalRelay.on(globalRelay.JOB_COST_SETTING_UPDATE, function (setting, jobCost) {
	if (setting.group === enums.jobCostSettingGroups.General && setting.name === 'taxJurisdiction') {
		const jurisdictions = globalConfig.jobCostSettings.tax.jurisdictions;
		const jurisdiction = _.find(jurisdictions, { name: setting.value });
		const taxRate = jurisdiction && jurisdiction.rate || 0;

		jobCost.setSetting(enums.jobCostSettingGroups.General, 'taxRate', undefined, taxRate);
	}
});

globalRelay.on(globalRelay.JOB_COST_BUILD_SUMMARY_LIST, function (jobCostSummary) {
	const jobCost = jobCostSummary.jobCost;
	const job = getModel({ collection: ModelCollection.jobs, key: jobCost.jobKey });

	// if this is a contract, the finalize process should have populated tax and warranty line items.
	if (jobCost.allowedContractEdits !== 'none') {
		const variant = jobCost.companyVariant;
		const taxConfig = globalConfig.jobCostSettings.tax;
		const jurisdictions = taxConfig.jurisdictions;
		const jurisdictionName = jobCost.getSettingValue(enums.jobCostSettingGroups.General, 'taxJurisdiction');
		const taxJurisdiction = _.find(jurisdictions, { name: jurisdictionName });
		const isTaxExempt = taxJurisdiction && taxJurisdiction.requiresTaxExemptContractor;
		const isCustomTax = taxJurisdiction && taxJurisdiction.custom;
		const baseTaxRate = Number(jobCost.getSettingValue(enums.jobCostSettingGroups.General, 'taxRate') || '0');
		let effectiveTaxRate = baseTaxRate;
		let orderTotalCost = jobCostSummary.orderTotalCost;
		const cabinetFreight = jobCostSummary.orderCabinetFreightCost;
		let itemAmount;

		// Trash Disposal
		const totalCabinetsLines = _.filter(jobCost.lineItems, { isCabinetCount: true });
		const totalCabinets = _.reduce(totalCabinetsLines, (sum, l) => sum + (l.quantity || 0), 0);
		itemAmount = util.roundAsCurrency(totalCabinets / 40 * 25);
		jobCostSummary.addItem({ cost: itemAmount, description: 'Trash Disposal', group: 'order', sellPrice: itemAmount, type: 'calculated-fee', reportingFlags: enums.jobCostLineItemReportingFlags.Pre_Subtotal });

		// Service & Warranty
		orderTotalCost = jobCostSummary.orderTotalCost; // reset the cost to include prior summary items
		if (orderTotalCost < 1000) {
			itemAmount = util.roundAsCurrency((orderTotalCost - cabinetFreight) * globalConfig.jobCostSettings.additionalServiceFee.under1000);
		}
		else if (orderTotalCost < 2500) {
			itemAmount = util.roundAsCurrency((orderTotalCost - cabinetFreight) * globalConfig.jobCostSettings.additionalServiceFee.under2500);
		}
		else {
			itemAmount = util.roundAsCurrency((orderTotalCost - cabinetFreight) * globalConfig.jobCostSettings.additionalServiceFee.over2500);
		}
		jobCostSummary.addItem({ cost: itemAmount, description: 'Service & Warranty', group: 'order', sellPrice: itemAmount, type: 'warranty', reportingFlags: enums.jobCostLineItemReportingFlags.Pre_Subtotal });

		// Processing Fee
		itemAmount = util.roundAsCurrency((orderTotalCost - cabinetFreight) * globalConfig.jobCostSettings.processingFee);
		jobCostSummary.addItem({ cost: itemAmount, description: 'Processing Fee', group: 'order', sellPrice: itemAmount, type: 'calculated-fee', reportingFlags: enums.jobCostLineItemReportingFlags.Pre_Subtotal });

		// CONDO PREMIUM
		if (jobCost.getSettingValue(enums.jobCostSettingGroups.General, 'applyCondoPremium') === '1') {
			jobCostSummary.addItem({
				cost: util.roundAsCurrency(0.5 * (jobCostSummary.orderInstallationCost + jobCostSummary.orderDeliveryCost)),
				description: 'Condo Premium',
				group: 'order',
				sellPrice: util.roundAsCurrency(util.costCalculator.getSellWithCostAndMargin(0.5 * (jobCostSummary.orderInstallationCost + jobCostSummary.orderDeliveryCost), jobCost.margin)),
				type: 'calculated-fee',
				reportingFlags: enums.jobCostLineItemReportingFlags.Pre_Subtotal
			});
		}

		// TAX
		if (!isTaxExempt) {
			const supplyOnly = jobCost.isSupplyOnly;
			let orderTaxedAmount = 0;

			if (supplyOnly) {
				const jurisdiction = _.find(jurisdictions, { supplyOnlyJurisdiction: variant });
				effectiveTaxRate = jurisdiction && jurisdiction.rate || effectiveTaxRate;
				// appliedPayments are not taxed
				orderTaxedAmount = util.roundAsCurrency(jobCostSummary.orderNetPrice);
			}
			else if (!job || job.builderKey || job.isRenovation) {
				// sold to builder or a remodel sold to homeowner - do not tax installation - only tax COST of product
				// leaving orderTaxedAmount = 0 prevents subsequent tax lines
				const effectiveTaxString = '(' + (effectiveTaxRate * 100).toFixed(4) + '%)';
				const taxRowSpecs = [{
					description: 'Cabinet Sales Tax ' + effectiveTaxString,
					relatedType: 'cabinet',
					cost: jobCostSummary.calculateTotalCost('order', ['cabinet'], -cabinetFreight)
				}, {
					description: 'Outside Supplier Sales Tax ' + effectiveTaxString,
					relatedType: 'outside-supplier',
					cost: jobCostSummary.calculateTotalCost('order', ['outside-supplier'])
				}, {
					description: 'Warehouse Sales Tax ' + effectiveTaxString,
					relatedType: 'warehouse',
					cost: jobCostSummary.calculateTotalCost('order', ['warehouse'])
				}, {
					description: 'Misc Sales Tax ' + effectiveTaxString,
					relatedType: 'misc',
					cost: jobCostSummary.calculateTotalCost('order', ['misc'])
				}, {
					description: 'Glass Sales Tax ' + effectiveTaxString,
					relatedType: 'special-order-glass',
					cost: jobCostSummary.calculateTotalCost('order', ['special-order-glass'])
				}];

				_.forEach(taxRowSpecs, spec => {
					const itemCost = spec.cost * effectiveTaxRate;
					if (itemCost !== 0) {
						const itemSell = util.roundAsCurrency(util.costCalculator.getSellWithCostAndMargin(itemCost, jobCost.margin));
						jobCostSummary.addItem({ cost: itemCost, description: spec.description, group: 'order', sellPrice: itemSell, relatedType: spec.relatedType, type: 'tax', costFactor: effectiveTaxRate, reportingFlags: (enums.jobCostLineItemReportingFlags.Contract_Hidden | enums.jobCostLineItemReportingFlags.Pre_Subtotal) });
					}
				});
			}
			else {
				// newHome sold to homeowner - tax at a fraction of standard rate
				if (!isCustomTax) {
					effectiveTaxRate *= taxConfig.newHomeFactor;
				}
				// appliedPayments are not taxed
				orderTaxedAmount = util.roundAsCurrency(jobCostSummary.orderNetPrice);
			}

			itemAmount = util.roundAsCurrency(orderTaxedAmount * effectiveTaxRate);
			if (itemAmount !== 0) {
				jobCostSummary.addItem({ cost: itemAmount, description: 'Sales Tax (' + (effectiveTaxRate * 100).toFixed(4) + '%)', group: 'order', sellPrice: itemAmount, type: 'tax', costFactor: effectiveTaxRate });
			}

			itemAmount = util.roundAsCurrency(jobCostSummary.clientUpgradeSellPrice * effectiveTaxRate);
			if (itemAmount) {
				jobCostSummary.addItem({ cost: itemAmount, description: 'Sales Tax (' + (effectiveTaxRate * 100).toFixed(4) + '%)', group: 'client-upgrade', sellPrice: itemAmount, type: 'tax', costFactor: effectiveTaxRate });
			}

			itemAmount = util.roundAsCurrency(jobCostSummary.subtradeTotalSellPrice * effectiveTaxRate);
			if (itemAmount !== 0) {
				jobCostSummary.addItem({ cost: itemAmount, description: 'Sales Tax (' + (effectiveTaxRate * 100).toFixed(4) + '%)', group: 'subtrade', sellPrice: itemAmount, type: 'tax', costFactor: effectiveTaxRate });
			}

			itemAmount = util.roundAsCurrency(jobCostSummary.solidSurfaceCounterTotalSellPrice * effectiveTaxRate);
			if (itemAmount !== 0) {
				jobCostSummary.addItem({ cost: itemAmount, description: 'Sales Tax (' + (effectiveTaxRate * 100).toFixed(4) + '%)', group: 'solid-surface-counter', sellPrice: itemAmount, type: 'tax', costFactor: effectiveTaxRate });
			}
		}
	}

	if (job) {
		// populate accumatica budget values
		const designRetainerAmount = jobCost.currentChangeOrder ? 0 : util.roundAsCurrency(_.filter(job.payments || [], payment => payment.type === 'design-retainer' && payment.appliedToContract && payment.jobCostKey === jobCost.key).reduce((sum, payment) => { sum += payment.amount; }, 0) / 1.05);
		for (const f of ACCOUNTING_SUMMARY_FACTORIES) {
			const item = f(jobCost, jobCostSummary, designRetainerAmount) as any;

			if ((item.type === 'budget-cost' && !!item.cost) || (item.type === 'budget-revenue' && !!item.sellPrice)) {
				item.cost = item.cost ? util.roundAsCurrency(item.cost) : item.cost;
				item.sellPrice = item.sellPrice ? util.roundAsCurrency(item.sellPrice) : item.sellPrice;

				jobCostSummary.addExtensionItem(item);
			}
		}
	}
});

globalRelay.registerGlobalFunction(WellKnownGlobalFunction.GET_LEAD_SOURCES, function () {
	const { leadSources } = store().getIn(['model', 'domainEntities']);

	return leadSources;
});

globalRelay.registerGlobalFunction('taxJurisdictions', function (context) {
	const jobCost = context.jobCost;
	const job = getModel({ collection: ModelCollection.jobs, key: jobCost.jobKey });
	let contractorPromise = Promise.resolve() as any;
	let jurisdictions = globalConfig.jobCostSettings.tax.jurisdictions;

	if (job) {
		if (job.workSiteAddress && ['az', 'arizona'].indexOf(job.workSiteAddress.region.toLowerCase()) !== -1) {
			jurisdictions = _.filter(jurisdictions, j => !j.outOfStateOnly);
		}

		if (job.builderKey) {
			const contractor = getModel({ collection: ModelCollection.contractors, key: job.builderKey });

			contractorPromise = contractor ? Promise.resolve(contractor) : contractorActions.fetchContractor({ key: job.builderKey });
		}
	}

	return contractorPromise.then(result => {
		if (!result || !result.isTaxExempt) {
			jurisdictions = _.filter(jurisdictions, j => !j.requiresTaxExemptContractor);
		}

		const options = _.map(jurisdictions, j => { return { value: j.name, label: j.name }; });

		options.unshift({ value: '', label: 'Select' });

		return options;
	});
});

globalRelay.registerGlobalFunction(WellKnownGlobalFunction.GET_JOB_OVERVIEW_DOCUMENTS, function () {
	const { leadSources } = store().getIn(['model', 'domainEntities']);

	return leadSources;
});

globalRelay.registerStateFilter('contractReports', (context, state) => {
	state.sections = _.filter(state.sections, r => ['contract/header'].indexOf(r.key) === -1);

	const agreementSection = _.find(state.sections, { key: 'contract/installagreement' });

	if (agreementSection) {
		agreementSection.name = 'Project Agreement';
		// warrantySection.key = 'contract/termsandconditions';
	}
});

globalRelay.registerStateFilter('paymentTermsConfig', (context, state) => {
	const jobCost = context.jobCost;
	const supplyOnly = jobCost.isSupplyOnly;

	if (supplyOnly) {
		state.termsConfig = globalConfig.jobCostSettings.paymentTerms.supplyOnly;
	}
});

globalRelay.registerStateFilter('finalizeAdditionalInfoConfig', (context, state) => {
	const { jobCost } = context;
	const oneWeek = 7 * 60 * 60 * 24 * 1000;
	const jurisdictions = globalConfig.jobCostSettings.tax.jurisdictions;
	const jurisdictionName = jobCost.getSettingValue(enums.jobCostSettingGroups.General, 'taxJurisdiction');
	const taxJurisdiction = _.find(jurisdictions, { name: jurisdictionName });
	const isTaxExempt = taxJurisdiction && taxJurisdiction.requiresTaxExemptContractor;
	const supplyOnly = jobCost.isSupplyOnly;

	state.additionalInfoConfig = { items: [] };

	if (isTaxExempt) {
		state.additionalInfoConfig.items.push({
			type: 'setting',
			itemKey: 'tax-exempt-form-confirmed',
			settingGroup: enums.jobCostSettingGroups.General,
			label: 'Do we have tax exempt form?',
			input: 'bool',
			isValid: val => val === '1'
		});
	}

	// if the jobCost was created with est-substantial-completion, use it, otherswise use the new delivery week range and estimated completion weeks
	if (jobCost && jobCost.getSetting(enums.jobCostSettingGroups.General, 'est-substantial-completion-offset')) {
		state.additionalInfoConfig.items.push({
			type: 'schedule-offset',
			itemKey: 'est-substantial-completion',
			label: supplyOnly ? 'Est. Order Pickup' : 'Est. Substantial Completion',
			input: 'select',
			offsets: _.map([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24], v => { return { label: (v.toString() + (v === 1 ? ' Week' : ' Weeks')), value: (v * oneWeek) }; }),
			isValid: val => val && !!val.date
		});
	}
	else {
		state.additionalInfoConfig.items.push({
			type: 'setting',
			itemKey: 'est-delivery-weeks-min',
			settingGroup: enums.jobCostSettingGroups.General,
			label: 'Est. Delivery Weeks Min',
			input: 'select',
			values: _.map([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30], v => { return { label: (v.toString() + (v === 1 ? ' Week' : ' Weeks')), value: v }; }),
			isValid: val => val && Number(val) >= 1 && Number(val) <= 30
		});

		state.additionalInfoConfig.items.push({
			type: 'setting',
			itemKey: 'est-delivery-weeks-max',
			settingGroup: enums.jobCostSettingGroups.General,
			label: 'Est. Delivery Weeks Max',
			input: 'select',
			values: _.map([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30], v => { return { label: (v.toString() + (v === 1 ? ' Week' : ' Weeks')), value: v }; }),
			isValid: val => val && Number(val) >= 1 && Number(val) <= 30
		});

		if (!supplyOnly) {
			state.additionalInfoConfig.items.push({
				type: 'setting',
				itemKey: 'est-substantial-completion-weeks',
				settingGroup: enums.jobCostSettingGroups.General,
				label: 'Est. Installation Window',
				input: 'select',
				helpText: 'Enter estimated weeks required for installation. Install cost / 1250 = install weeks, rounded up.',
				values: _.map([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30], v => { return { label: (v.toString() + (v === 1 ? ' Week' : ' Weeks')), value: v }; }),
				isValid: val => val && Number(val) >= 1 && Number(val) <= 30
			});
		}
	}

	// if the jobCost was created with drawingPageCount use it, otherwise use the newer start/end/date settings
	if (jobCost && jobCost.getSetting(enums.jobCostSettingGroups.General, 'drawingPageCount')) {
		state.additionalInfoConfig.items.push({
			type: 'setting',
			itemKey: 'drawingPageCount',
			settingGroup: enums.jobCostSettingGroups.General,
			label: 'Drawing Page Count',
			input: 'numeric',
			isValid: val => val && Number(val) > 0
		});
	}
	else {
		state.additionalInfoConfig.items.push({
			type: 'setting',
			itemKey: 'drawingFirstPage',
			settingGroup: enums.jobCostSettingGroups.General,
			label: 'Drawing First Page',
			input: 'text',
			isValid: val => !!val
		});

		state.additionalInfoConfig.items.push({
			type: 'setting',
			itemKey: 'drawingLastPage',
			settingGroup: enums.jobCostSettingGroups.General,
			label: 'Drawing Last Page',
			input: 'text',
			isValid: val => !!val
		});

		state.additionalInfoConfig.items.push({
			type: 'setting',
			itemKey: 'drawingPrintDate',
			settingGroup: enums.jobCostSettingGroups.General,
			label: 'Drawing Print Date',
			input: 'date',
			isValid: val => !!val
		});
	}
});